/**
 * Field baker: a closed outline (`d` string, stage space) → a signed distance grid a shader samples.
 * Pipeline: flatten beziers → seed outline cells at sub-texel spacing → exact feature transform (nearest seed cell) →
 * exact point-to-edge distance, from the nearest seed's edge refined by a chain walk and an 8-neighbour check →
 * sign by the shape's fill rule (raster.js#fillMask, inside positive) → [flow u/v: geodesic, flow.js] → dilate g/b outward to the nearest inside
 * texel.
 *
 * The grid is the outline's bounds padded by PAD_TEXELS on every side, so a bilinear tap at the outline never reads
 * past the texture: uv = (world − origin) / (texel · [width, height]), texel centers at (i + 0.5) / size.
 */
import { timed } from '../stageTiming'
import { createFieldCache } from './cache'
import { nearestSeed } from './edt'
import { flowChannels } from './flow'
import { coreChannel, outlineCorners, ringTurn, writeTurn } from './phase'
import {
	boundingPolys,
	boundsOf,
	EDGE_STRIDE,
	edgeDist2,
	edgeLinks,
	edgesOf,
	fillMask,
	flattenPath,
	hullBounds,
	seedEdges,
} from './raster'

/** Flattening tolerance as a fraction of a texel — the curve sits within this of its polyline. */
const FLATNESS = 0.05
/** Border around the outline's bounds, in texels: covers the wrapper's `r < −1` discard and the coverage smoothstep. */
export const PAD_TEXELS = 2
/**
 * Module-local binding for the hot loops: vitest's module runner turns an imported name into a getter on a namespace
 * object per call, which blocks inlining and doubles the bake time under test. Browsers and the build are unaffected.
 */
const segDist2 = edgeDist2

/**
 * Per-cell working buffers, kept across bakes (a bake is synchronous, so one set serves every caller) and grown to
 * the largest grid seen: ≈ 26 bytes a cell, 6.8 MB at 512². Allocating them fresh cost more than the EDT.
 */
let pool = null
function scratch(n) {
	if (!pool || pool.seed.length < n) {
		pool = {
			seed: new Int32Array(n),
			nearest: new Int32Array(n),
			edge: new Int32Array(n),
			dist2: new Float64Array(n),
			inside: new Uint8Array(n),
			flag: new Uint8Array(n),
			row: new Int32Array(n),
		}
	}
	return pool
}

/**
 * Grid over `bounds` at ≈ 1 stage px per texel, long side (padding included) capped at `maxSize`.
 * @returns {{ width: number, height: number, origin: [number, number], texel: number }}
 */
export function fieldGrid(bounds, maxSize = 512, pad = PAD_TEXELS) {
	const [minX, minY, maxX, maxY] = bounds
	const w = maxX - minX
	const h = maxY - minY
	const inner = Math.max(1, maxSize - 2 * pad)
	const texel = Math.max(1, Math.max(w, h) / inner)
	const fit = v => Math.min(inner, Math.max(1, Math.ceil(v / texel - 1e-9)))
	const width = fit(w) + 2 * pad
	const height = fit(h) + 2 * pad
	// Center the outline in the grid so the ceil slack splits evenly on both sides.
	const ox = (minX + maxX) / 2 - (width * texel) / 2
	const oy = (minY + maxY) / 2 - (height * texel) / 2
	return { width, height, origin: [ox, oy], texel }
}

/**
 * @param {string} d closed outline, stage space
 * @param {{ flow?: { from: number, to: number } | null, maxSize?: number }} [opts]
 * @returns {{ data: Float32Array, width: number, height: number, origin: [number, number], texel: number,
 *   inradius: number, perimeter: number, extent: [number, number], flow: boolean } | null} null when `d` has no closed area to bake.
 *   `data` RGBA per cell: r = signed distance (inside positive, stage units), g/b = flow u/v, or without `flow` asked (cos, sin) of the turn along the cell's ring (phase.js), a = distance to the core (phase.js), stage units.
 *   `inradius` = max r over cell centers, ≥ 0 (within ~0.7 texel of the true inradius; 0 for a sliver with no
 *   inside cell). `perimeter` = the longest closed polyline's length. `extent` = the outline's flattened bounds size [w, h] in stage units — a placeholder: no consumer
 *   reads it for a linear frame (those carry their own extent). With a `flow` that resolves (flow.js#flowEnds:
 *   ≥ 4 segments, `from`/`to` wrapped, neither equal nor adjacent): g = u (0 at segment `from` → 1 at `to`, geodesic),
 *   b = v (0 on side A = segments from+1 … to−1, 1 on side B, ½ on the medial line), dilated past the outline, and
 *   `extent` = [area ÷ W, W], W = median bank-to-bank geodesic width — river length × mean width, and `flow` is
 *   true. Otherwise (no `flow` asked, endpoints refused, or an end chain that borders no inside cell, e.g. the tip
 *   of a hairline spike) `flow` is false, g/b hold the ring turn and `extent` is the placeholder: draw such a field as `edge`.
 */
export function bakeField(d, { flow = null, maxSize = 512 } = {}) {
	const hull = hullBounds(d)
	if (!hull) return null
	const hullTexel = Math.max(1, Math.max(hull[2] - hull[0], hull[3] - hull[1]) / maxSize)
	const segInfo = flow ? {} : null // edge → segment map, for the flow seeds
	const polys = flattenPath(d, FLATNESS * hullTexel, segInfo)
	const bounds = boundsOf(polys)
	if (!bounds || !(bounds[2] - bounds[0] > 0) || !(bounds[3] - bounds[1] > 0)) return null

	const grid = fieldGrid(bounds, maxSize)
	const { width, height, texel } = grid
	const [ox, oy] = grid.origin
	const n = width * height
	const { seed, nearest, edge, dist2, inside: insideBuf, flag, row } = scratch(n)
	const outline = boundingPolys(polys, segInfo)
	if (!outline.length) return null
	const edges = edgesOf(outline)
	const links = edgeLinks(outline)
	seedEdges(edges, grid, seed, dist2) // dist2 doubles as the seeding's best-distance buffer
	nearestSeed(seed, width, height, nearest, edge) // edge doubles as the EDT's row scratch
	const inside = fillMask(polys, edges, grid, insideBuf)
	walkPass(edges, links, seed, nearest, grid, edge, dist2, flag)
	neighbourPass(edges, links, grid, edge, dist2, flag)

	const data = new Float32Array(n * 4)
	let maxR = 0
	for (let c = 0; c < n; c++) {
		const dist = Math.sqrt(dist2[c])
		if (inside[c]) {
			data[c * 4] = dist
			if (dist > maxR) maxR = dist
		} else data[c * 4] = -dist
	}
	let extent = [bounds[2] - bounds[0], bounds[3] - bounds[1]]
	let flowed = false
	const corners = outlineCorners(edges, EDGE_STRIDE, outline, (x, y) => {
		const cx = Math.floor((x - ox) / texel)
		const cy = Math.floor((y - oy) / texel)
		return cx >= 0 && cy >= 0 && cx < width && cy < height && inside[cy * width + cx] === 1
	})
	coreChannel(data, width, height, texel, maxR, seed, nearest, row)
	if (flow) {
		// Reads `edge`; borrows dist2 / flag / seed / nearest as scratch.
		const flowExtent = flowChannels(data, inside, grid, edges, segInfo, flow, { edge, dist2, flag, seed, nearest })
		flowed = !!flowExtent && flowExtent.every(x => x > 0 && x < Infinity)
		if (flowed) {
			extent = flowExtent
			// Last step: it reuses seed / nearest / edge as scratch.
			dilateChannels(data, inside, width, height, { seed, nearest, edge })
		} else clearFlow(data, n) // flowChannels can bail after writing u
	}
	// A refused flow draws as `edge`, so it carries the ring turn too.
	if (!flowed)
		for (let c = 0; c < n; c++) {
			if (edge[c] < 0) continue
			const px = ox + ((c % width) + 0.5) * texel
			const py = oy + (Math.floor(c / width) + 0.5) * texel
			writeTurn(data, c, ringTurn(corners, edges, EDGE_STRIDE, links, edge[c], px, py, data[c * 4]))
		}
	return {
		data,
		width,
		height,
		origin: [ox, oy],
		texel,
		inradius: maxR,
		perimeter: corners.perimeter,
		extent,
		flow: flowed,
	}
}

const clearFlow = (data, n) => {
	for (let c = 0; c < n; c++) {
		data[c * 4 + 1] = 0
		data[c * 4 + 2] = 0
	}
}

/**
 * Copy g/b of every outside cell from its nearest inside cell (exact, by center distance, via the feature
 * transform), so bilinear taps and the coverage band just past the outline read the edge's flow, not zero.
 * Inside cells and r/a are untouched. No inside cell → nothing changes. O(cells).
 * @param {Float32Array} data RGBA field
 * @param {Uint8Array} inside 1 inside, per cell
 * @param {{ seed?: Int32Array, nearest?: Int32Array, edge?: Int32Array }} [buf] scratch, each ≥ width · height
 */
export function dilateChannels(data, inside, width, height, buf = {}) {
	const n = width * height
	const seeds = buf.seed || new Int32Array(n)
	for (let c = 0; c < n; c++) seeds[c] = inside[c] ? c : -1
	const near = nearestSeed(seeds, width, height, buf.nearest, buf.edge)
	for (let c = 0; c < n; c++) {
		if (inside[c]) continue
		const s = near[c]
		if (s < 0) return // no inside cell anywhere
		data[c * 4 + 1] = data[s * 4 + 1]
		data[c * 4 + 2] = data[s * 4 + 2]
	}
}

// A pure module (no app logger at import in a worker): `console.scope` when the app installed it.
const cacheLogger = typeof console.scope === 'function' ? console.scope('surfaceField') : console
const cache = createFieldCache((...args) => timed('bake', () => bakeField(...args)), undefined, {
	onOverBudget: ({ bytes, budget, pinned }) =>
		cacheLogger.warn(
			`warm set over budget (${(bytes / 2 ** 20).toFixed(1)} of ${(budget / 2 ** 20).toFixed(0)} MB, ${pinned} pinned): falling back to LRU`
		),
})

/**
 * The field for a resolved outline (`resolveOutline` → `{ d, key }`) and optional flow endpoints, baked once per
 * `outline.key` + flow (+ `maxSize`) and kept LRU within 64 MB of field data (cache.js). Shared: never mutate the result.
 * @param {{ d: string, key: string } | null} outline
 * @param {{ from: number, to: number } | null} [flow]
 * @param {number} [maxSize] a coarser grid than `bakeField`'s default, for a draft
 */
export const fieldFor = (outline, flow = null, maxSize) => cache.get(outline, flow, maxSize)
/** @returns {{ entries: number, bytes: number, keys: string[] }} keys oldest first — for the probe and tests */
export const fieldCacheStats = () => cache.stats()
/** The warm keys currently baked (pinned against LRU eviction). */
export const pinnedFieldKeys = () => cache.pinnedKeys()
/** Whether a field key (`fieldKey(outline, flow)`) is baked, without baking it or touching its recency. */
export const hasField = key => cache.has(key)
/** Pin the warm set: every field key a cueable preset draws. Replaces the previous set (cache.js `pin`). */
export const pinFields = keys => cache.pin(keys)
export const clearFieldCache = () => cache.clear()

/** True when edge `o` is neither `e` nor (by index) one of its chain neighbours. A polyline's wrap reads as far. */
const far = (o, e) => o - e + 1 > 2 || o - e + 1 < 0

/**
 * Pass 1 — per cell, the edge its nearest seed holds, then that edge's chain walked downhill. The seed is
 * nearest only to within the ~0.7 texel a cell center sits off the outline, and seen from deep inside a curve the
 * whole arc is nearly equidistant, so the true nearest edge can be a few links along. Fills `edge` / `dist2`, and
 * flags both cells of every 8-neighbour pair whose edges are far apart on the chain — pass 2's work list.
 */
function walkPass(edges, links, seed, nearest, grid, edge, dist2, flag) {
	const { width, height, texel } = grid
	const [ox, oy] = grid.origin
	const { next, prev } = links
	flag.fill(0, 0, width * height)
	for (let y = 0; y < height; y++) {
		const py = oy + (y + 0.5) * texel
		for (let x = 0; x < width; x++) {
			const c = y * width + x
			const px = ox + (x + 0.5) * texel
			let e = seed[nearest[c]]
			let best = segDist2(edges, e, px, py)
			// Walk first toward the end the foot clamps to (that neighbour is at least as near); the other way only
			// if the first did not move.
			const o = e * EDGE_STRIDE
			const before = (px - edges[o]) * edges[o + 2] + (py - edges[o + 1]) * edges[o + 3] <= 0
			const first = before ? prev : next
			const start = e
			let f = first[e]
			let d2 = segDist2(edges, f, px, py)
			while (d2 < best && f !== start) {
				best = d2
				e = f
				f = first[f]
				d2 = segDist2(edges, f, px, py)
			}
			if (e === start) {
				const second = before ? next : prev
				f = second[e]
				d2 = segDist2(edges, f, px, py)
				while (d2 < best && f !== start) {
					best = d2
					e = f
					f = second[f]
					d2 = segDist2(edges, f, px, py)
				}
			}
			edge[c] = e
			dist2[c] = best
			// Pairs with the already-filled neighbours: left, and the three above.
			if (x > 0 && far(edge[c - 1], e)) flag[c] = flag[c - 1] = 1
			if (y > 0) {
				const u = c - width
				if (far(edge[u], e)) flag[c] = flag[u] = 1
				if (x > 0 && far(edge[u - 1], e)) flag[c] = flag[u - 1] = 1
				if (x < width - 1 && far(edge[u + 1], e)) flag[c] = flag[u + 1] = 1
			}
		}
	}
}

/**
 * Pass 2 — across a medial axis the seed can sit on the wrong side; at each flagged cell, every 8-neighbour holding
 * an edge other than this cell's own or its chain neighbours (pass 1 measured those) offers it. 8, not 4: where
 * three branches of the medial axis meet, only a diagonal may hold the right edge. In place, so a fix carries on.
 * Refines `edge` / `dist2`.
 */
function neighbourPass(edges, links, grid, edge, dist2, flag) {
	const { width, height, texel } = grid
	const [ox, oy] = grid.origin
	const { next, prev } = links
	const nb = new Int32Array(8)
	for (let y = 0; y < height; y++) {
		const py = oy + (y + 0.5) * texel
		const up = y > 0 ? -width : 0
		const down = y < height - 1 ? width : 0
		for (let x = 0; x < width; x++) {
			const c = y * width + x
			if (!flag[c]) continue
			const e = edge[c]
			const en = next[e]
			const ep = prev[e]
			const left = x > 0 ? -1 : 0
			const right = x < width - 1 ? 1 : 0
			nb[0] = edge[c + left]
			nb[1] = edge[c + right]
			nb[2] = edge[c + up]
			nb[3] = edge[c + down]
			nb[4] = edge[c + up + left]
			nb[5] = edge[c + up + right]
			nb[6] = edge[c + down + left]
			nb[7] = edge[c + down + right]
			const px = ox + (x + 0.5) * texel
			let best = dist2[c]
			let bestEdge = e
			for (let k = 0; k < 8; k++) {
				const o = nb[k]
				if (o === e || o === en || o === ep || o === bestEdge) continue
				const d2 = segDist2(edges, o, px, py)
				if (d2 < best) {
					best = d2
					bestEdge = o
				}
			}
			edge[c] = bestEdge
			dist2[c] = best
		}
	}
}
